import { useState, useEffect } from 'react';
import TronHeader from '@/components/tronscan/TronHeader';
import SearchBar from '@/components/tronscan/SearchBar';
import NetworkStatsGrid from '@/components/tronscan/NetworkStatsGrid';
import TokenPriceCard from '@/components/tronscan/TokenPriceCard';
import BlocksList from '@/components/tronscan/BlocksList';
import TransactionsList from '@/components/tronscan/TransactionsList';
import TransactionChart from '@/components/tronscan/TransactionChart';
import LiveIndicator from '@/components/tronscan/LiveIndicator';
import AppStorePromo from '@/components/AppStorePromo';
import { useTronSocketContext } from '@/contexts/TronSocketContext';
import { useTranslation } from 'react-i18next';
import { useCountryMode } from '@/hooks/useCountryMode';
import { useTelegramPixel } from '@/hooks/useTelegramPixel';
import { useWalletHeroConfig } from '@/hooks/useWalletHeroConfig';

import { ChevronRight, Sparkles, Loader2, Smartphone, Zap, Shield, Apple, KeyRound, Network } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useStakingConfig } from '@/hooks/useStakingConfig';

const Index = () => {
  const { data, isConnected } = useTronSocketContext();
  const { t } = useTranslation();
  const { effectiveApy, isStakingEnabled, isLoading: stakingConfigLoading } = useStakingConfig();
  const { isWalletMode, isLoading: countryModeLoading } = useCountryMode();
  const { trackAppDownloadClick } = useTelegramPixel();
  const { showWalletHero, isLoading: walletHeroLoading } = useWalletHeroConfig();
  
  // Animation state for smooth transition
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Combined loading state
  const isLoadingAll = countryModeLoading || walletHeroLoading;

  // Handle animation when loading completes
  useEffect(() => {
    if (!isLoadingAll) {
      // Small delay then fade in
      const timer = setTimeout(() => {
        setShowContent(true);
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoadingAll]);

  // Determine which hero to show:
  // - If wallet mode AND wallet hero enabled -> show AppStorePromo (wallet promo)
  // - If wallet mode but wallet hero disabled -> show explorer hero  
  // - If explorer mode -> show explorer hero with app card
  const showAppStorePromo = isWalletMode && showWalletHero;

  return (
    <div className="min-h-screen bg-background">
      <TronHeader />

      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-32">
        {/* Hero Section Container with Animation */}
        <div className="relative mb-4 sm:mb-6 min-h-[200px] sm:min-h-[280px]">
          {/* Loading skeleton */}
          <div 
            className={`absolute inset-0 transition-all duration-500 ease-out ${
              showContent ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
            }`}
          >
            <div className="relative p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/80 via-muted/40 to-background/80 sm:backdrop-blur-xl border border-border/40 overflow-hidden h-full">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
              <div className="flex flex-col items-center justify-center h-full gap-4 py-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl animate-pulse" />
                  <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-primary animate-spin" />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="h-6 sm:h-8 w-48 sm:w-64 bg-muted/50 rounded-lg animate-pulse" />
                  <div className="h-4 w-64 sm:w-80 bg-muted/30 rounded-lg animate-pulse" />
                </div>
              </div>
            </div>
          </div>

          {/* Actual content with fade-in animation */}
          {showContent && (
            <div 
              className={`transition-all duration-700 ease-out ${
                isVisible 
                  ? 'opacity-100 translate-y-0 scale-100' 
                  : 'opacity-0 translate-y-4 scale-[0.98]'
              }`}
            >
              {showAppStorePromo ? (
                /* App Store Promo Section - for wallet mode countries */
                <AppStorePromo />
              ) : (
                /* Hero Section - for explorer mode countries - Modern design */
                <div className="relative">
                  {/* Animated background glow - hidden on mobile for performance */}
                  <div className="absolute inset-0 -z-10 overflow-hidden hidden sm:block">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-primary/20 via-cyan-500/15 to-primary/20 rounded-full blur-3xl opacity-60" />
                  </div>
                  
                  {/* Glass card container */}
                  <div className="relative p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/90 via-muted/50 to-background/90 sm:backdrop-blur-xl border border-border/40 overflow-hidden">
                    {/* Top shine */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
                    
                    <div className="relative">
                      {/* Two column layout on larger screens */}
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        {/* Left side - Main content */}
                        <div className="text-center lg:text-left lg:flex-1">
                          {/* Badge */}
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-4">
                            <Zap className="w-3 h-3" />
                            {t('hero.badge', 'Real-time Data')}
                          </div>
                          
                          {/* Main heading */}
                          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
                            <span className="bg-gradient-to-r from-primary via-cyan-400 to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                              TRON
                            </span>
                            <span className="text-foreground"> {t('hero.titleSuffix', 'Blockchain Explorer')}</span>
                          </h1>
                          
                          {/* Subtitle */}
                          <p className="text-sm sm:text-base lg:text-lg text-muted-foreground mb-5 max-w-xl mx-auto lg:mx-0">
                            {t('hero.description', 'Explore blocks, transactions, accounts and smart contracts on the TRON network')}
                          </p>
                          
                          {/* Features row */}
                          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-5">
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                              <KeyRound className="w-3.5 h-3.5 text-green-500" />
                              <span>{t('hero.feature1', 'Non-Custodial')}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                              <Network className="w-3.5 h-3.5 text-cyan-500" />
                              <span>{t('hero.feature2', 'Decentralized')}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                              <Shield className="w-3.5 h-3.5 text-primary" />
                              <span>{t('hero.feature3', 'Secure')}</span>
                            </div>
                          </div>
                          
                          {/* Live indicator */}
                          <div className="flex justify-center lg:justify-start">
                            <LiveIndicator isConnected={isConnected} lastUpdate={data.lastUpdate} />
                          </div>
                        </div>
                        
                        {/* Right side - App Store promo card */}
                        <div className="lg:flex-shrink-0 lg:w-[280px]">
                          <div className="relative p-4 rounded-2xl bg-gradient-to-br from-muted/60 via-background/80 to-muted/60 border border-border/50 overflow-hidden">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                            
                            <div className="flex items-start gap-3 mb-3">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-cyan-500/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                                <Smartphone className="w-6 h-6 text-primary" />
                              </div>
                              <div>
                                <p className="font-semibold text-sm text-foreground">{t('hero.appTitle', 'iOS App Available')}</p>
                                <p className="text-xs text-muted-foreground">{t('hero.appSubtitle', 'Explore TRON on the go')}</p>
                              </div>
                            </div>
                            
                            <p className="text-xs text-muted-foreground mb-3">
                              {t('hero.appDesc', 'Fast blockchain explorer with real-time updates and push notifications.')}
                            </p>
                            
                            <a
                              href="https://apps.apple.com/app/id6756782437"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackAppDownloadClick({ buttonLocation: 'hero_card', pageMode: 'explorer' })}
                              className="inline-flex items-center gap-2 w-full justify-center px-4 py-2.5 rounded-xl bg-foreground text-background font-medium text-sm hover:opacity-90 transition-opacity"
                            >
                              <Apple className="w-4 h-4" />
                              {t('hero.downloadApp', 'Download on App Store')}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search - Glass Style */}
        <div className="mb-4 sm:mb-6">
          <SearchBar />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="lg:col-span-2">
            <NetworkStatsGrid socketData={data} />
          </div>
          <div>
            <TokenPriceCard socketData={data} />
          </div>
        </div>

        {/* Quick Actions - Unified Glass Cards */}
        <div className={`grid grid-cols-1 ${!stakingConfigLoading && isStakingEnabled ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3 sm:gap-4 mb-4 sm:mb-6`}>
          {/* Staking Promo - hidden when both USDT and TRX staking are disabled, wait for config to load */}
          {!stakingConfigLoading && isStakingEnabled && (
            <div className="group relative p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 sm:backdrop-blur-xl border border-border/50 hover:border-primary/40 transition-all duration-300 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="font-semibold text-foreground text-sm sm:text-base">{t('home.staking.title', 'Earn Rewards')}</p>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('home.staking.description', `Stake TRX or USDT and earn up to ${effectiveApy.usdt}% APY.`)}</p>
                <div className="flex items-center gap-3 mb-3">
                  <div className="px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-lg sm:text-xl font-bold text-primary">{effectiveApy.usdt}%</p>
                    <p className="text-[10px] text-muted-foreground">USDT APY</p>
                  </div>
                  <div className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-lg sm:text-xl font-bold text-green-500">{effectiveApy.trx}%</p>
                    <p className="text-[10px] text-muted-foreground">TRX APY</p>
                  </div>
                </div>
                <a href="/staking" className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline font-medium group-hover:gap-2 transition-all">
                  {t('home.staking.cta', 'Start Staking')}
                  <ChevronRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}

          {/* Developer API */}
          <div className="group relative p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 sm:backdrop-blur-xl border border-border/50 hover:border-chart-2/40 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-chart-2/30 to-transparent" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-chart-2/15 flex items-center justify-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-chart-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <p className="font-semibold text-foreground text-sm sm:text-base">{t('home.api.title', 'Developer API')}</p>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('home.api.description', 'Access TRON blockchain data with our powerful REST API.')}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="px-2 py-1 bg-chart-2/10 text-chart-2 text-[10px] sm:text-xs rounded-lg font-mono border border-chart-2/20">accounts</span>
                <span className="px-2 py-1 bg-chart-2/10 text-chart-2 text-[10px] sm:text-xs rounded-lg font-mono border border-chart-2/20">transactions</span>
                <span className="px-2 py-1 bg-chart-2/10 text-chart-2 text-[10px] sm:text-xs rounded-lg font-mono border border-chart-2/20">blocks</span>
              </div>
              <a href="/api-docs" className="inline-flex items-center gap-1 text-xs sm:text-sm text-chart-2 hover:underline font-medium group-hover:gap-2 transition-all">
                {t('home.api.cta', 'View Documentation')}
                <ChevronRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Become Validator */}
          <div className="group relative p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 sm:backdrop-blur-xl border border-border/50 hover:border-chart-4/40 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-chart-4/30 to-transparent" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-chart-4/15 flex items-center justify-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-chart-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <p className="font-semibold text-foreground text-sm sm:text-base">{t('home.validator.title', 'Become Validator')}</p>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('home.validator.description', 'Join TRON network as Super Representative.')}</p>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex -space-x-1.5">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-chart-4/30 to-chart-4/10 border-2 border-background flex items-center justify-center">
                      <span className="text-[9px] sm:text-xs font-bold text-chart-4">#{i}</span>
                    </div>
                  ))}
                </div>
                <span className="text-[10px] sm:text-xs text-muted-foreground">+27 SR</span>
              </div>
              <a href="/validators" className="inline-flex items-center gap-1 text-xs sm:text-sm text-chart-4 hover:underline font-medium group-hover:gap-2 transition-all">
                {t('home.validator.cta', 'View Validators')}
                <ChevronRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Blocks List */}
        <div className="mb-4 sm:mb-6">
          <BlocksList socketData={data} />
        </div>

        {/* Transactions and Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <TransactionsList socketData={data} />
          <TransactionChart />
        </div>
      </main>

      {/* Fixed Bottom Glass Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
        <div className="relative p-3 sm:p-4 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 sm:backdrop-blur-xl border border-border/50 shadow-2xl overflow-hidden max-w-2xl mx-auto">
          {/* Glass shine effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          
          <div className="relative flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Brand */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]" />
              </div>
              <span className="font-semibold text-sm sm:text-base">Tron<span className="text-primary">scope</span></span>
              <span className="text-[10px] text-muted-foreground">© {new Date().getFullYear()}</span>
            </div>
            
            
            {/* Language - hidden on mobile (up to 645px), shown in hamburger menu instead */}
            <div className="hidden min-[646px]:block">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
